import { useCallback, useEffect, useMemo, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import aa from "../AdminPortal/adminApp.module.css";
import { Button, useToast } from "../../ui";
import {
  competitionResultLabel,
  getPlacementMedal,
  getPlacementLabel,
  isCompetitionResultMarked,
  PARTICIPATED_ICON,
  PARTICIPATED_LABEL,
  QUICK_PLACEMENTS,
} from "../../../utils/competitionResultUtils";

const STATUS_LABEL = {
  pending: "На рассмотрении",
  approved: "Допущен",
  rejected: "Отклонён",
  cancelled: "Отмена",
};

function sortForResults(rows) {
  const approved = rows.filter((r) => r.status === "approved");
  const pending = rows.filter((r) => r.status === "pending");
  const other = rows.filter(
    (r) => !["approved", "pending"].includes(r.status),
  );

  const byPlacement = (a, b) => {
    const aMarked = isCompetitionResultMarked(a);
    const bMarked = isCompetitionResultMarked(b);
    if (aMarked && !bMarked) return -1;
    if (!aMarked && bMarked) return 1;
    if (a.result_status === "no_show" && b.result_status !== "no_show") return 1;
    if (b.result_status === "no_show" && a.result_status !== "no_show") return -1;
    if (a.result_status === "participated" && b.result_status === "placed") return 1;
    if (b.result_status === "participated" && a.result_status === "placed") return -1;
    const ap = a.placement ?? 999;
    const bp = b.placement ?? 999;
    if (ap !== bp) return ap - bp;
    return (a.athlete?.full_name || "").localeCompare(
      b.athlete?.full_name || "",
      "ru",
    );
  };

  return {
    approved: [...approved].sort(byPlacement),
    pending,
    other,
  };
}

export default function CompetitionJournalResults({ sessionId, onUpdated }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await clinicAPI.getSessionRegistrations(sessionId);
      setRows(data.registrations || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки списка");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => sortForResults(rows), [rows]);

  const saveResult = async (registrationId, payload) => {
    setSavingId(registrationId);
    try {
      await clinicAPI.updateSessionRegistration(sessionId, registrationId, payload);
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось сохранить");
    } finally {
      setSavingId(null);
    }
  };

  const setPlacement = (registrationId, placement) => {
    saveResult(registrationId, { result_status: "placed", placement });
  };

  const setParticipated = (registrationId) => {
    saveResult(registrationId, { result_status: "participated" });
  };

  const setNoShow = (registrationId) => {
    saveResult(registrationId, { result_status: "no_show" });
  };

  const clearResult = (registrationId) => {
    saveResult(registrationId, { result_status: "none" });
  };

  const renderApprovedRow = (r) => {
    const busy = savingId === r.id;
    const resultLabel = competitionResultLabel(r);
    const marked = isCompetitionResultMarked(r);
    const isParticipated =
      r.result_status === "participated" ||
      (r.result_status === "placed" && Number(r.placement) > 3);

    return (
      <li key={r.id} className={aa.journalCompetitionResultItem}>
        <div className={aa.journalCompetitionResultMain}>
          <strong className={aa.journalCompetitionResultName}>
            {r.athlete?.full_name || "—"}
          </strong>
          {r.athlete_weight_kg || r.athlete_age ? (
            <span className={aa.journalCompetitionResultMeta}>
              {[
                r.athlete_weight_kg ? `${r.athlete_weight_kg} кг` : null,
                r.athlete_age ? `${r.athlete_age} лет` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          ) : null}
          {resultLabel ? (
            <span
              className={`${aa.journalCompetitionResultBadge} ${
                r.result_status === "no_show"
                  ? aa.journalCompetitionResultBadgeNoShow
                  : isParticipated
                    ? aa.journalCompetitionResultBadgeParticipated
                    : aa.journalCompetitionResultBadgePlace
              }`}
            >
              {resultLabel}
            </span>
          ) : (
            <span className={aa.journalCompetitionResultBadgeMuted}>
              Результат не отмечен
            </span>
          )}
        </div>

        <div className={aa.journalCompetitionResultActions}>
          <div className={aa.journalCompetitionPlaceRow}>
            {QUICK_PLACEMENTS.map((place) => {
              const medal = getPlacementMedal(place);
              const isOn =
                r.result_status === "placed" && r.placement === place;
              return (
                <button
                  key={place}
                  type="button"
                  className={`${aa.journalCompetitionMedalBtn} ${
                    isOn ? aa.journalCompetitionMedalBtnOn : ""
                  }`}
                  disabled={busy}
                  onClick={() => setPlacement(r.id, place)}
                  title={getPlacementLabel(place)}
                  aria-label={getPlacementLabel(place)}
                >
                  <span className={aa.journalCompetitionMedalIcon}>{medal}</span>
                </button>
              );
            })}
            <button
              type="button"
              className={`${aa.journalCompetitionMedalBtn} ${
                isParticipated ? aa.journalCompetitionParticipatedBtnOn : ""
              }`}
              disabled={busy}
              onClick={() => setParticipated(r.id)}
              title={PARTICIPATED_LABEL}
              aria-label={PARTICIPATED_LABEL}
            >
              <span className={aa.journalCompetitionMedalIcon} aria-hidden>
                {PARTICIPATED_ICON}
              </span>
            </button>
          </div>

          <div className={aa.journalCompetitionResultSecondary}>
            <Button
              type="button"
              size="sm"
              variant={r.result_status === "no_show" ? "primary" : "secondary"}
              disabled={busy}
              onClick={() => setNoShow(r.id)}
            >
              Не пришёл
            </Button>
            {marked ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => clearResult(r.id)}
              >
                Сбросить
              </Button>
            ) : null}
          </div>
        </div>
      </li>
    );
  };

  if (loading) {
    return (
      <p className={aa.journalCompetitionResultsEmpty}>Загрузка участников…</p>
    );
  }

  if (rows.length === 0) {
    return (
      <p className={aa.journalCompetitionResultsEmpty}>
        Нет заявок. Добавьте участников в карточке соревнования в календаре.
      </p>
    );
  }

  return (
    <div className={aa.journalCompetitionResults}>
      <div className={aa.journalCompetitionResultsHead}>
        <h3 className={aa.journalCompetitionResultsTitle}>Итоги</h3>
        <p className={aa.journalCompetitionResultsHint}>
          В профиль — медали за 1–3 место. Участие без призового места — 🏅
        </p>
      </div>

      {grouped.approved.length === 0 ? (
        <p className={aa.journalCompetitionResultsEmpty}>
          Нет допущенных участников — сначала одобрите заявки в календаре.
        </p>
      ) : (
        <ul className={aa.journalCompetitionResultsList}>
          {grouped.approved.map(renderApprovedRow)}
        </ul>
      )}

      {grouped.pending.length > 0 ? (
        <div className={aa.journalCompetitionPendingBlock}>
          <h4 className={aa.journalCompetitionPendingTitle}>
            На рассмотрении ({grouped.pending.length})
          </h4>
          <ul className={aa.journalCompetitionPendingList}>
            {grouped.pending.map((r) => (
              <li key={r.id} className={aa.journalCompetitionPendingItem}>
                <span>{r.athlete?.full_name || "—"}</span>
                <span className={aa.journalCompetitionPendingStatus}>
                  {STATUS_LABEL[r.status]}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
